<template>
  <section aria-labelledby="messaging-title">
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
      <div>
        <h2 id="messaging-title">Messaging Protection</h2>
        <p>Automatic checks before SMS and email are sent. Higher order volume alone never blocks messages.</p>
      </div>
      <button class="btn btn-green" :disabled="loading" @click="load">Refresh</button>
    </div>
    <p v-if="error" role="alert" class="alert alert-warning">{{ error }} Protection status cannot be confirmed.</p>
    <p v-if="loading && !result" role="status">Checking protection…</p>
    <template v-if="result">
      <div class="card p-4 mb-4" role="status">
        <h3 class="h5">{{ status }}</h3>
        <p v-if="connected">The view updates automatically when protection data changes.</p>
        <p v-else role="status">Live updates are disconnected. Displayed data may be outdated; use Refresh to check again.</p>
        <p class="mb-1">Last mailer check ({{ result.timezone }}): {{ result.state?.mailerSeenAt || 'No check received' }}</p>
        <p class="mb-1">{{ result.state?.mailerPending || 0 }} unsent requests awaiting a connection to protection.</p>
        <p class="mb-0">
          Held messages remain held after a restart or rollback. Evaluation rules do not control this protection.
        </p>
      </div>
      <div v-if="latest" class="card p-4 mb-4">
        <h3 class="h5">Latest release · {{ latest.repository }}</h3>
        <p>{{ latest.sha.slice(0, 8) }} · Monitoring started {{ latest.createdAt }}</p>
        <p>
          {{
            latest.deployedAt
              ? 'Deployment completed. Monitoring continues.'
              : 'Deployment completion has not been confirmed.'
          }}
        </p>
        <p>
          {{
            latest.schedulerObserved
              ? 'A pickup scheduler run has been observed after deployment.'
              : 'Awaiting a confirmed pickup scheduler run after deployment.'
          }}
        </p>
        <div class="row g-3">
          <div v-for="channel in channels" :key="channel" class="col-md-6">
            <h4 class="h6">{{ messageChannelLabel(channel) }}</h4>
            <p>
              {{ count(channel, 'accepted') }} accepted · {{ count(channel, 'held') }} held ·
              {{ count(channel, 'sending') + count(channel, 'unknown') }} outcomes unresolved
            </p>
          </div>
        </div>
        <details>
          <summary>Historical context — never a sending limit</summary>
          <p>
            Eight preceding calendar weeks, excluding the deployment day. Historical errors and missing notifications
            can affect these figures.
          </p>
          <ul>
            <li v-for="row in latest.baseline" :key="`${row.type}-${row.eventType}`">
              {{ row.type }} · {{ row.eventType }}: {{ row.attempts }} attempts over {{ row.activeDays }} active days.
            </li>
          </ul>
        </details>
      </div>
      <p v-else>No releases have been registered yet. This does not mean a deployment has been checked.</p>
      <h3 class="h5">Recent message checks</h3>
      <p>Provider acceptance is not proof of delivery. Unresolved attempts cannot be resent from this screen.</p>
      <p>Message check timestamps: {{ result.timezone }}.</p>
      <EasyDataTable
        ref="dataTable"
        v-model:server-options="serverOptions"
        :server-items-length="result.intents.count"
        :items="result.intents.data"
        :headers="headers"
        :loading="loading"
        :rows-per-page="protectionPageSize"
        :rows-items="[protectionPageSize]"
        table-class-name="vue3-easy-data-table table-shadow no-overflow"
        alternating
        hide-footer
        @update:server-options="updateServerOptions"
      >
        <template #empty-message>No message checks recorded yet.</template>
        <template #item-createdAt="row">{{ row.createdAt }}</template>
        <template #item-channel="row">
          {{ messageChannelLabel(row.channel) }}<br />{{ row.shipmentNumber || 'No shipment reference' }}
        </template>
        <template #item-status="row">{{ messagingStatusLabel(row.status) }}</template>
        <template #item-reason="row">{{ deploymentReasonLabel(row.reason) }}</template>
        <template #item-action="row">
          <button v-if="row.status === 'held'" class="btn btn-outline-secondary" @click="select(row)">
            Review
          </button>
        </template>
      </EasyDataTable>
      <TablePagination
        v-model:server-options-ref="serverOptions"
        v-model:data-table-ref="dataTable"
        :inert="loading"
        :server-items-length="result.intents.count"
      />
      <section v-if="selected" class="card p-4 mb-4" aria-labelledby="review-title">
        <h3 id="review-title">Review held {{ messageChannelLabel(selected.channel) }}</h3>
        <p>A recheck only schedules the message when it is valid now. It is checked again before sending.</p>
        <label for="review-reason">Reason for this action (do not include recipient details)</label>
        <textarea id="review-reason" v-model="comment" class="form-control mb-3" maxlength="1000" rows="3" />
        <div class="d-flex gap-3 flex-wrap">
          <button class="btn btn-green" :disabled="!canReview" @click="recheck">Recheck eligibility</button>
          <button class="btn btn-outline-danger" :disabled="!canReview" @click="cancel">Cancel message</button>
          <button class="btn btn-outline-secondary" :disabled="saving" @click="closeReview">Close</button>
        </div>
        <p v-if="reviewError" role="alert">{{ reviewError }}</p>
      </section>
      <p v-if="notice" role="status">{{ notice }}</p>
      <details class="card p-4 mb-4">
        <summary>Release history</summary>
        <ul>
          <li v-for="release in result.releases" :key="release.id">
            {{ release.createdAt }} · {{ release.repository }} · {{ release.sha.slice(0, 8) }} · {{ release.status }}
          </li>
        </ul>
      </details>
    </template>
  </section>
</template>

<script lang="ts">
import type { MessagingProtectionResponse } from '@/types/Api/MessagingProtection'
import type {
  MessagingIntent,
  ProtectedRelease,
  MessageChannel,
  MessagingStatus,
} from '@/types/Models/MessagingProtection'
import type { ComputedRef, Ref } from 'vue'
import { computed, defineComponent, ref, watch, onBeforeUnmount } from 'vue'
import { useMessagingProtectionUpdates } from '@/composables/useMessagingProtectionUpdates'
import { protectionPageSize } from '@/const/MessagingProtection'
import { messageChannels } from '@/const/MessagingProtection'
import {
  messageChannelLabel,
  messagingStatusLabel,
  deploymentReasonLabel,
  messagingReviewNotice,
} from '@/utils/messagingProtection'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'
import MessagingProtectionProxy from '@/proxies/MessagingProtectionProxy'

export default defineComponent({
  name: 'MessagingProtection',
  components: { EasyDataTable, TablePagination },
  setup() {
    const dataTable: Ref<InstanceType<typeof EasyDataTable> | null> = ref(null)
    const serverOptions: Ref<ServerOptions> = ref({ page: 1, rowsPerPage: protectionPageSize })
    const loading: Ref<boolean> = ref(false)
    const error: Ref<string> = ref('')

    const result: Ref<MessagingProtectionResponse | null> = ref(null)
    const selected: Ref<MessagingIntent | null> = ref(null)
    const saving: Ref<boolean> = ref(false)
    const comment: Ref<string> = ref('')
    const reviewError: Ref<string> = ref('')
    const notice: Ref<string> = ref('')
    const latest: ComputedRef<ProtectedRelease | undefined> = computed(() => result.value?.releases[0])
    const channels: MessageChannel[] = Object.values(messageChannels)
    const mailerReady: Ref<boolean> = ref(false)
    let readinessTimer: ReturnType<typeof setTimeout> | undefined
    const status: ComputedRef<string> = computed(() => {
      if (error.value) return 'Protection status unavailable'
      if (!result.value?.enabled) return 'Protection is not enabled in this environment'
      if (!connected.value) return 'Live updates disconnected · current readiness unconfirmed'
      if (!result.value.state?.mailerSeenAt) return 'Mailer check missing — investigate'
      if (!mailerReady.value) return 'Mailer check stale — investigate'
      return 'Protection enabled · mailer check current'
    })
    const headers: Header[] = [
      { text: 'Time', value: 'createdAt' },
      { text: 'Channel / shipment', value: 'channel' },
      { text: 'Result', value: 'status' },
      { text: 'Reason', value: 'reason' },
      { text: 'Action', value: 'action' },
    ]

    const count = (channel: MessageChannel, state: MessagingStatus): number =>
      Number(latest.value?.counts.find((row) => row.channel === channel && row.status === state)?.attempts || 0)

    const load = async (): Promise<void> => {
      if (loading.value) return
      loading.value = true
      try {
        result.value = await new MessagingProtectionProxy({ page: serverOptions.value.page }).all()
        error.value = ''
      } catch {
        result.value = null
        error.value = 'Unable to read deployment protection.'
      } finally {
        loading.value = false
      }
    }

    const updateServerOptions = (options: ServerOptions): void => {
      serverOptions.value = options
      void refresh()
    }

    const select = (row: MessagingIntent): void => {
      selected.value = row
      comment.value = ''
      reviewError.value = ''
      notice.value = ''
    }

    const closeReview = (): void => {
      selected.value = null
      reviewError.value = ''
    }

    const canReview: ComputedRef<boolean> = computed(() =>
      Boolean(selected.value && !saving.value && comment.value.trim()),
    )

    const recheck = async (): Promise<void> => {
      if (!canReview.value || !selected.value) return
      saving.value = true
      reviewError.value = ''
      try {
        const response = await new MessagingProtectionProxy().recheck(selected.value.id, comment.value)
        notice.value = messagingReviewNotice(response.status)
        closeReview()
        await refresh()
      } catch {
        reviewError.value = 'The action failed. Your comment has been kept; refresh before retrying.'
      } finally {
        saving.value = false
      }
    }

    const cancel = async (): Promise<void> => {
      if (!canReview.value || !selected.value) return
      saving.value = true
      reviewError.value = ''
      try {
        const response = await new MessagingProtectionProxy().cancel(selected.value.id, comment.value)
        notice.value = messagingReviewNotice(response.status)
        closeReview()
        await refresh()
      } catch {
        reviewError.value = 'The action failed. Your comment has been kept; refresh before retrying.'
      } finally {
        saving.value = false
      }
    }

    const { connected, refresh } = useMessagingProtectionUpdates('deployment', load)
    watch(result, (value) => {
      clearTimeout(readinessTimer)
      const seconds = Math.max(0, Math.min(300, value?.mailerReadyForSeconds ?? 0))
      mailerReady.value = seconds > 0
      if (seconds > 0) {
        readinessTimer = setTimeout(() => {
          mailerReady.value = false
          void refresh()
        }, seconds * 1000)
      }
    })
    onBeforeUnmount(() => clearTimeout(readinessTimer))

    return {
      result,
      selected,
      loading,
      error,
      saving,
      comment,
      reviewError,
      notice,
      latest,
      status,
      channels,
      headers,
      dataTable,
      serverOptions,
      protectionPageSize,
      count,
      load: refresh,
      connected,
      updateServerOptions,
      select,
      closeReview,
      recheck,
      cancel,
      canReview,
      messageChannelLabel,
      messagingStatusLabel,
      deploymentReasonLabel,
    }
  },
})
</script>
